import { useCallback } from 'react'
import { useRecipientsSelectContext } from 'components/CIMSSelect/common/RecipientsSelect/RecipientsSelectContext'
import { simpleEditNotification } from 'hooks/notificationHandler'
import { useUpsertRfiNotificationMutation } from 'schema'
import { DefaultMutationHookOptions } from 'utils/defaults'
import { useRFINotificationsContext } from '../../RFINotificationsContext'
import { EditNotificationEventOptionsFn } from 'components/CIMSNotifications/types'
import { LabeledValueType } from 'components/CIMSSelect/types'
import { ByResponseDisciplinesFormItemValue } from './components/RenderFormItems/components/ByResponseDisciplinesFormItem/types'
import { Permission } from '@cims-online/shared'
import RenderFormItems from './components/RenderFormItems'
import { useProjectContext } from 'context/ProjectContext'

export function useEdit() {
  const { projectId } = useProjectContext()
  const { recipients, refetch } = useRFINotificationsContext()
  const { toLabeledValues, fromLabeledValues } = useRecipientsSelectContext()
  const [edit] = useUpsertRfiNotificationMutation({
    ...DefaultMutationHookOptions,
    onCompleted: ({
      upsertRFINotification: {
        notification: { notificationEvent },
      },
    }) => simpleEditNotification(notificationEvent),
  })
  return useCallback<EditNotificationEventOptionsFn<InitialValues>>(
    (event) => {
      const { every, byResponseDiscipline } = recipients(event)
      return {
        permission: Permission.EditRFINotification,
        initialValues: {
          every: toLabeledValues(every),
          responseDisciplines: byResponseDiscipline.map(
            ({ responseDiscipline, recipients }) => ({
              responseDisciplineId: responseDiscipline.id,
              recipients: toLabeledValues(recipients),
            })
          ),
        },
        onClear: async () => {
          await edit({
            variables: {
              input: {
                notificationEventId: event.id,
                projectId,
                every: { userIds: [], nonUserEmailIds: [] },
                byResponseDiscipline: [],
              },
            },
          })
        },
        onSave: async ({ every, responseDisciplines }) => {
          await edit({
            variables: {
              input: {
                notificationEventId: event.id,
                projectId,
                every: fromLabeledValues(every),
                byResponseDiscipline: responseDisciplines.map(
                  ({ responseDisciplineId, recipients }) => ({
                    responseDisciplineId,
                    recipients: fromLabeledValues(recipients),
                  })
                ),
              },
            },
          })
          refetch()
        },
        render: <RenderFormItems />,
      }
    },
    [edit, fromLabeledValues, projectId, recipients, refetch, toLabeledValues]
  )
}

type InitialValues = {
  every: LabeledValueType[]
  responseDisciplines: ByResponseDisciplinesFormItemValue[]
}
